/**
 * ProjectGeneralSection - a project's identity: name, description, icon and
 * accent colour.
 *
 * The first tab of Settings → Proyectos, and selected by default, because
 * these are the facts about a project someone reaches for before touching its
 * repository, its board or its links — the ones that answer "which project is
 * this" rather than "how does it work".
 *
 * Read-only without `p.edit`: the fields render, nothing is editable, exactly
 * like the other tabs on this screen gate themselves per project.
 *
 * The colour is saved the moment it is picked — there is nothing to draft,
 * unlike the name and description, which share one "Guardar" so a half-typed
 * description is never submitted by itself.
 */
import type { ProjectId } from "@taskin-code/contracts";
import { PROJECT_DESCRIPTION_MAX_LENGTH } from "@taskin-code/contracts";
import { useEffect, useRef, useState } from "react";

import { AuthClient } from "../../auth/AuthClient";
import { useHasProjectPermission } from "../../auth/authStore";
import {
  normalizeProjectColorInput,
  PROJECT_COLOR_PALETTE,
  resolveProjectColor,
} from "../../lib/projectIdentity";
import { readFileAsDataUrl } from "../../lib/readFileAsDataUrl";
import { newCommandId } from "../../lib/utils";
import { readNativeApi } from "../../nativeApi";
import { useStore } from "../../store";
import type { Project } from "../../types";
import { ProjectAvatar } from "../ProjectAvatar";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { toastManager } from "../ui/toast";
import { AVATAR_FILE_INPUT_ACCEPT, validateAvatarFile } from "./ProfileSection.logic";
import { buildProjectMetaUpdate, isProjectGeneralFormValid } from "./ProjectGeneralSection.logic";

export function ProjectGeneralSection(props: { readonly project: Project }) {
  const { project } = props;
  const canEdit = useHasProjectPermission(project.id as ProjectId, "p.edit");

  return (
    <div className="flex flex-col gap-5">
      <IdentityForm project={project} canEdit={canEdit} />
      <IconCard project={project} canEdit={canEdit} />
      <ColorCard project={project} canEdit={canEdit} />
    </div>
  );
}

function dispatchProjectMetaUpdate(
  projectId: ProjectId,
  fields: Record<string, unknown>,
): Promise<unknown> {
  const api = readNativeApi();
  if (!api) return Promise.reject(new Error("No hay conexión con el servidor."));
  return api.orchestration.dispatchCommand({
    type: "project.meta.update",
    commandId: newCommandId(),
    projectId,
    ...fields,
  });
}

function IdentityForm(props: { readonly project: Project; readonly canEdit: boolean }) {
  const { project, canEdit } = props;
  const [title, setTitle] = useState(project.name);
  const [description, setDescription] = useState(project.description ?? "");
  const [saving, setSaving] = useState(false);

  // Server state is the default view; landing on a different project (or a
  // rename arriving from elsewhere) resets any unsaved draft.
  useEffect(() => {
    setTitle(project.name);
    setDescription(project.description ?? "");
  }, [project.id, project.name, project.description]);

  const form = { title, description };
  const update = buildProjectMetaUpdate(project, form);
  const hasChanges = Object.keys(update).length > 0;
  const canSave = canEdit && hasChanges && isProjectGeneralFormValid(form) && !saving;

  const save = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSave) return;
    setSaving(true);
    try {
      await dispatchProjectMetaUpdate(project.id as ProjectId, update);
      toastManager.add({ type: "success", title: "Cambios guardados." });
    } catch (err) {
      toastManager.add({
        type: "error",
        title: "No se pudo guardar el proyecto.",
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={(event) => void save(event)}
      className="flex flex-col gap-4 rounded-xl border border-border/60 bg-card/40 p-5"
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`project-title-${project.id}`}>Nombre</Label>
        <Input
          id={`project-title-${project.id}`}
          value={title}
          disabled={!canEdit || saving}
          required
          onChange={(event) => setTitle(event.target.value)}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <Label htmlFor={`project-description-${project.id}`}>Descripción</Label>
          <span className="text-[11px] text-muted-foreground">
            {description.length}/{PROJECT_DESCRIPTION_MAX_LENGTH}
          </span>
        </div>
        <Textarea
          id={`project-description-${project.id}`}
          value={description}
          disabled={!canEdit || saving}
          maxLength={PROJECT_DESCRIPTION_MAX_LENGTH}
          rows={3}
          placeholder="Para qué sirve este proyecto, en una o dos frases."
          onChange={(event) => setDescription(event.target.value)}
        />
      </div>

      {canEdit ? (
        <div>
          <Button type="submit" size="sm" disabled={!canSave}>
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          Necesitas el permiso <code>p.edit</code> en este proyecto para cambiar su nombre o
          descripción.
        </p>
      )}
    </form>
  );
}

function IconCard(props: { readonly project: Project; readonly canEdit: boolean }) {
  const { project, canEdit } = props;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const onFilePicked = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Cleared immediately so picking the same file twice still fires a change
    // event, letting a failed upload be retried without choosing another file.
    event.target.value = "";
    if (!file) return;

    const verdict = validateAvatarFile(file);
    if (!verdict.ok) {
      toastManager.add({
        type: "error",
        title: "No se pudo subir la imagen.",
        description: verdict.message,
      });
      return;
    }

    void (async () => {
      setBusy(true);
      try {
        const dataUrl = await readFileAsDataUrl(file);
        await AuthClient.setProjectIcon(project.id as ProjectId, dataUrl);
        toastManager.add({ type: "success", title: "Icono actualizado." });
      } catch (err) {
        toastManager.add({
          type: "error",
          title: "No se pudo subir la imagen.",
          description: err instanceof Error ? err.message : undefined,
        });
      } finally {
        setBusy(false);
      }
    })();
  };

  const remove = () =>
    void (async () => {
      setBusy(true);
      try {
        await AuthClient.removeProjectIcon(project.id as ProjectId);
        toastManager.add({ type: "success", title: "Icono eliminado." });
      } catch (err) {
        toastManager.add({
          type: "error",
          title: "No se pudo quitar la imagen.",
          description: err instanceof Error ? err.message : undefined,
        });
      } finally {
        setBusy(false);
      }
    })();

  return (
    <section className="flex flex-col gap-3 rounded-xl border border-border/60 bg-card/40 p-5">
      <div className="min-w-0">
        <h4 className="text-[15px] font-semibold tracking-tight">Icono</h4>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Se muestra en el sidebar y en cualquier lugar donde aparezca el proyecto. Sin uno propio,
          se usa el favicon del repositorio o las iniciales del nombre.
        </p>
      </div>

      <div className="flex items-center gap-4">
        <ProjectAvatar project={project} size="md" className="size-12 text-base" />

        {canEdit ? (
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept={AVATAR_FILE_INPUT_ACCEPT}
              className="hidden"
              onChange={onFilePicked}
            />
            <Button
              type="button"
              size="sm"
              disabled={busy}
              onClick={() => fileInputRef.current?.click()}
            >
              {busy ? "Subiendo…" : "Subir imagen"}
            </Button>
            {project.iconFileName ? (
              <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={remove}>
                Quitar
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}

function ColorCard(props: { readonly project: Project; readonly canEdit: boolean }) {
  const { project, canEdit } = props;
  const projects = useStore((state) => state.projects);
  const current = projects.find((entry) => entry.id === project.id) ?? project;
  const [hexDraft, setHexDraft] = useState(current.color ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setHexDraft(current.color ?? "");
  }, [current.color]);

  const applyColor = (color: string | null) =>
    void (async () => {
      setSaving(true);
      try {
        await dispatchProjectMetaUpdate(project.id as ProjectId, { color });
        toastManager.add({ type: "success", title: "Color actualizado." });
      } catch (err) {
        toastManager.add({
          type: "error",
          title: "No se pudo guardar el color.",
          description: err instanceof Error ? err.message : undefined,
        });
      } finally {
        setSaving(false);
      }
    })();

  const applyHex = () => {
    const normalized = normalizeProjectColorInput(hexDraft);
    if (normalized === null) {
      toastManager.add({
        type: "error",
        title: "Color inválido.",
        description: "Usa un formato hexadecimal, como #2563eb.",
      });
      return;
    }
    applyColor(normalized);
  };

  return (
    <section className="flex flex-col gap-3 rounded-xl border border-border/60 bg-card/40 p-5">
      <div className="min-w-0">
        <h4 className="text-[15px] font-semibold tracking-tight">Color</h4>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Distingue este proyecto de los demás en el sidebar. Sin uno propio, se deriva del nombre.
        </p>
      </div>

      <p className="text-sm font-medium" style={{ color: resolveProjectColor(current) }}>
        {current.name}
      </p>

      {canEdit ? (
        <>
          <div className="flex flex-wrap gap-2">
            {PROJECT_COLOR_PALETTE.map((entry) => (
              <button
                key={entry.value}
                type="button"
                aria-label={entry.label}
                disabled={saving}
                onClick={() => applyColor(entry.value)}
                className={`size-7 rounded-md ring-offset-2 ring-offset-background transition-shadow ${
                  current.color === entry.value ? "ring-2 ring-foreground" : ""
                }`}
                style={{ backgroundColor: entry.value }}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            <Input
              value={hexDraft}
              disabled={saving}
              placeholder="#2563eb"
              className="w-32"
              onChange={(event) => setHexDraft(event.target.value)}
            />
            <Button type="button" size="sm" variant="outline" disabled={saving} onClick={applyHex}>
              Aplicar
            </Button>
            {current.color !== null ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={saving}
                onClick={() => applyColor(null)}
              >
                Sin color
              </Button>
            ) : null}
          </div>
        </>
      ) : null}
    </section>
  );
}
